/**
 * Org member roster sync (people with or without logins).
 */

import { getSupabaseClient, hasSupabaseConfig } from '../../lib/supabaseClient';

export function formatRosterName(person) {
  if (!person) return 'User';
  const first = String(person.first_name || person.firstName || '').trim();
  const last = String(person.last_name || person.lastName || '').trim();
  if (first || last) return [first, last].filter(Boolean).join(' ');
  const display = String(person.display_name || person.displayName || '').trim();
  if (display) return display;
  return String(person.email || 'User').trim() || 'User';
}

function mapRosterRow(row) {
  return {
    id: row.id,
    organizationId: row.organization_id,
    firstName: row.first_name || '',
    lastName: row.last_name || '',
    email: row.email || '',
    userId: row.user_id || null,
    isActive: row.is_active !== false,
    displayName: formatRosterName({
      first_name: row.first_name,
      last_name: row.last_name,
      email: row.email,
    }),
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export async function listOrgRoster(organizationId, { activeOnly = true } = {}) {
  if (!hasSupabaseConfig() || !organizationId) return [];
  const supabase = getSupabaseClient();
  if (!supabase) return [];
  let q = supabase
    .from('org_roster_members')
    .select(
      'id, organization_id, first_name, last_name, email, user_id, is_active, created_at, updated_at'
    )
    .eq('organization_id', organizationId)
    .order('last_name', { ascending: true })
    .order('first_name', { ascending: true });
  if (activeOnly) q = q.eq('is_active', true);
  const { data, error } = await q;
  if (error) throw error;
  return (data || []).map(mapRosterRow);
}

export async function createRosterMember(organizationId, { firstName, lastName, email = null, userId = null }) {
  if (!hasSupabaseConfig() || !organizationId) throw new Error('Not configured');
  const supabase = getSupabaseClient();
  const first = String(firstName || '').trim();
  const last = String(lastName || '').trim();
  if (!first || !last) throw new Error('First and last name are required');
  const { data, error } = await supabase
    .from('org_roster_members')
    .insert({
      organization_id: organizationId,
      first_name: first,
      last_name: last,
      email: email ? String(email).trim() : null,
      user_id: userId || null,
      is_active: true,
    })
    .select('*')
    .single();
  if (error) throw error;
  return mapRosterRow(data);
}

export async function updateRosterMember(rosterId, patch = {}) {
  if (!hasSupabaseConfig() || !rosterId) throw new Error('Not configured');
  const supabase = getSupabaseClient();
  const payload = { updated_at: new Date().toISOString() };
  if (patch.firstName != null) payload.first_name = String(patch.firstName).trim();
  if (patch.lastName != null) payload.last_name = String(patch.lastName).trim();
  if (patch.email !== undefined) payload.email = patch.email ? String(patch.email).trim() : null;
  if (patch.userId !== undefined) payload.user_id = patch.userId || null;
  if (patch.isActive !== undefined) payload.is_active = Boolean(patch.isActive);
  if (payload.first_name === '' || payload.last_name === '') {
    throw new Error('First and last name are required');
  }
  const { data, error } = await supabase
    .from('org_roster_members')
    .update(payload)
    .eq('id', rosterId)
    .select('*')
    .single();
  if (error) throw error;
  return mapRosterRow(data);
}

export async function ensureSelfOnOrgRoster(organizationId, firstName, lastName) {
  if (!hasSupabaseConfig() || !organizationId) return null;
  const supabase = getSupabaseClient();
  if (!supabase) return null;
  const { data, error } = await supabase.rpc('ensure_self_on_org_roster', {
    p_organization_id: organizationId,
    p_first_name: firstName || null,
    p_last_name: lastName || null,
  });
  if (error) {
    console.warn('[rosterSync] ensure_self_on_org_roster failed', error.message);
    return null;
  }
  return data;
}

export function buildDisplayName(firstName, lastName) {
  return [String(firstName || '').trim(), String(lastName || '').trim()].filter(Boolean).join(' ');
}
